import Input from '@components/V4/Inputs/Input';
import InputLabel from '@components/V4/Inputs/InputLabel';
import Textarea from '@components/V4/Inputs/Textarea';
import Select, { SelectOption } from '@components/V4/Select/Select';
import ToggleButton from '@components/V4/ToggleButton';
import { CourseEditableFields, LmsCatalogCourseDTO } from '@shared/apis/courses.apis';
import { COLORS } from 'apps/agora/src/v3/global';
import { ReactNode } from 'react';
import { CirclePicker } from 'react-color';
import SunEditor from 'suneditor-react';
import 'suneditor/src/assets/css/suneditor.css';
import { COUNTRY_OPTIONS, CourseSwatch, lmsAccessLabel } from './courseUi';

export type CourseFormValues = CourseEditableFields;

export const EMPTY_COURSE_FORM: CourseFormValues = {
  title: '',
  subtitle: '',
  abbreviation: '',
  lmsCourseId: '',
  lmsCoursePrice: 0,
  isFree: true,
  isMentorless: false,
  isPublic: false,
  country: '',
  color: COLORS.PRIMARY,
  price: 0,
  youtubeId: '',
  description: '',
  details: '',
};

export const lmsCourseOptions = (catalog: LmsCatalogCourseDTO[] = []): SelectOption[] => [
  { label: 'No LMS course', value: '' },
  ...[...catalog]
    .sort((a, b) => a.title.localeCompare(b.title))
    .map((course) => ({
      value: course.id,
      label: `${course.title} · ${lmsAccessLabel(course.access)}${
        course.price ? ` · ${course.price}$` : ''
      }${course.content?.isEmpty ? ' · EMPTY' : ''}`,
    })),
];

const ToggleRow = (props: {
  label: string;
  hint: string;
  isChecked: boolean;
  onToggle: () => void;
}) => (
  <div className="flex items-center justify-between gap-4 rounded-xl bg-white/5 px-4 py-3">
    <div className="flex flex-col">
      <span className="text-sm font-medium text-white">{props.label}</span>
      <span className="text-xs text-customGrey">{props.hint}</span>
    </div>
    <ToggleButton isChecked={props.isChecked} onClick={props.onToggle} />
  </div>
);

const Section = (props: { title: string; description?: string; children: ReactNode }) => (
  <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
    <div className="flex flex-col gap-0.5">
      <h3 className="text-base font-semibold text-white">{props.title}</h3>
      {props.description ? <p className="text-xs text-customGrey">{props.description}</p> : null}
    </div>
    {props.children}
  </div>
);

type Props = {
  value: CourseFormValues;
  onChange: (patch: Partial<CourseFormValues>) => void;
  lmsCatalog: LmsCatalogCourseDTO[];
  isLmsCatalogLoading?: boolean;
  mode: 'create' | 'edit';
};

const CourseForm = (props: Props) => {
  const { value, onChange, lmsCatalog, isLmsCatalogLoading, mode } = props;

  const selectLmsCourse = (lmsCourseId: string) =>
    onChange({
      lmsCourseId,
      lmsCoursePrice: lmsCatalog.find((course) => course.id === lmsCourseId)?.price ?? 0,
    });

  return (
    <div className="flex flex-col gap-4">
      <Section title="Identity" description="What students, mentors and parents see in Agora.">
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
          <Input
            type="text"
            id="course-title"
            name="title"
            label="Title"
            placeholder="e.g. Bocconi Admission Prep"
            value={value.title}
            isRequired
            onChange={(event) => onChange({ title: event.target.value })}
          />
          <Input
            type="text"
            id="course-subtitle"
            name="subtitle"
            label="Subtitle"
            placeholder="One line shown under the title"
            value={value.subtitle}
            onChange={(event) => onChange({ subtitle: event.target.value })}
          />
          <div className="flex flex-col gap-1">
            <Input
              type="text"
              id="course-abbreviation"
              name="abbreviation"
              label="Abbreviation"
              placeholder="B, US, LNAT…"
              value={value.abbreviation}
              maxLength={5}
              onChange={(event) => onChange({ abbreviation: event.target.value.toUpperCase() })}
            />
            <span className="text-xs text-customGrey">
              Shown on session cards. Universities use one letter (B), CoachMe uses the country (US,
              UK, EU), exam prep uses the exam (SAT, LNAT).
            </span>
          </div>
          <Select
            label="Targeted country"
            variant="secondary"
            size="large"
            value={value.country ?? ''}
            options={COUNTRY_OPTIONS}
            onSelect={(selected) => onChange({ country: String(selected ?? '') })}
          />
        </div>

        <div className="flex flex-col gap-2">
          <InputLabel label="Color" className="text-white text-xsm" />
          <div className="flex flex-wrap items-center gap-5">
            <CirclePicker
              colors={[COLORS.PRIMARY, COLORS.PURPLE, COLORS.GOLD, '#34d399', '#f87171', '#60a5fa']}
              color={value.color}
              onChange={(picked) => onChange({ color: picked.hex })}
            />
            <div className="flex items-center gap-3">
              <div className="w-32">
                <Input
                  type="text"
                  id="course-color"
                  name="color"
                  label="Hex"
                  value={value.color}
                  onChange={(event) => onChange({ color: event.target.value })}
                />
              </div>
              <CourseSwatch color={value.color} abbreviation={value.abbreviation} size="large" />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="LMS course"
        description="The LearnWorlds course students are enrolled in when they start this course. Changing it on production re-syncs every active student's LMS enrollment."
      >
        <Select
          label="Linked LMS course"
          variant="secondary"
          size="large"
          showSearch
          isLoading={isLmsCatalogLoading}
          value={value.lmsCourseId ?? ''}
          options={lmsCourseOptions(lmsCatalog)}
          onSelect={(selected) => selectLmsCourse(String(selected ?? ''))}
        />
      </Section>

      <Section title="Access">
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2">
          <ToggleRow
            label="Free course"
            hint="Check even if only a few lessons are free."
            isChecked={value.isFree}
            onToggle={() => onChange({ isFree: !value.isFree })}
          />
          <ToggleRow
            label="Mentorless"
            hint="Self-paced course with no mentor sessions."
            isChecked={value.isMentorless}
            onToggle={() => onChange({ isMentorless: !value.isMentorless })}
          />
          {mode === 'edit' ? (
            <Input
              type="number"
              id="course-price"
              name="price"
              label="Price (€)"
              value={value.price ?? 0}
              onChange={(event) => onChange({ price: Number(event.target.value) || 0 })}
            />
          ) : null}
        </div>
      </Section>

      {mode === 'edit' ? (
        <Section
          title="Presentation"
          description="Optional marketing content shown in the catalog and to parents."
        >
          <Input
            type="text"
            id="course-youtube"
            name="youtubeId"
            label="YouTube presentation video id"
            placeholder="e.g. dQw4w9WgXcQ"
            value={value.youtubeId ?? ''}
            onChange={(event) => onChange({ youtubeId: event.target.value })}
          />
          <Textarea
            id="course-description"
            name="description"
            label="Short description for parents"
            placeholder="Plain text shown when a parent expands the course in their timeline."
            rows={4}
            value={value.description ?? ''}
            onChange={(event) => onChange({ description: event.target.value })}
          />
          <div className="flex flex-col gap-2">
            <InputLabel label="Catalog details (rich text)" className="text-white text-xsm" />
            <div className="rounded-xl bg-white text-black">
              <SunEditor
                defaultValue={value.details ?? ''}
                onChange={(content) => onChange({ details: content })}
                setOptions={{ minHeight: '200px' }}
              />
            </div>
          </div>
        </Section>
      ) : null}
    </div>
  );
};

export default CourseForm;
